import React from 'react';

const DetailedInvestigationView = ({ data, type }) => {
  if (!data) {
    return (
      <div className="p-4 text-center">
        <p>Loading detailed investigation data...</p>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-600">{data.error}</p>
      </div>
    );
  }

  const trendsData = data.trendsData || [];

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">
          {data.isHitRate ? 'Hit Rate Investigation Summary' : 'Trends Investigation Summary'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">Target Item</div>
            <div className="font-medium">
              {type === 'product' ? data.product : 
               type === 'location' ? data.locationName : 
               data.sku}
            </div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">Total Records</div>
            <div className="text-lg font-bold text-blue-600">
              {trendsData.length}
            </div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">
              {data.isHitRate ? 'Avg Hit Rate' : 'Avg Accuracy'}
            </div>
            <div className={`text-lg font-bold ${
              trendsData.length ? 
                (trendsData.reduce((sum, item) => sum + item.accuracy, 0) / trendsData.length) < 80 ? 'text-red-600' : 
                (trendsData.reduce((sum, item) => sum + item.accuracy, 0) / trendsData.length) < 95 ? 'text-yellow-600' : 'text-green-600'
              : 'text-gray-600'
            }`}>
              {trendsData.length ? 
                (trendsData.reduce((sum, item) => sum + item.accuracy, 0) / trendsData.length).toFixed(1) + '%'
              : 'N/A'}
            </div>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600">
              {data.isHitRate ? 'Hit Rate Value' : 'Net Value'}
            </div>
            <div className={`text-lg font-bold ${
              data.isHitRate ? 'text-green-600' : 
              trendsData.reduce((sum, item) => sum + (item.varianceValue || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {data.isHitRate ? 
                `${data.accuracy?.toFixed(1)}%` : 
                `$${Math.abs(trendsData.reduce((sum, item) => sum + (item.varianceValue || 0), 0)).toFixed(2)}`
              }
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Data Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b">
          <h4 className="font-medium">
            {data.isHitRate ? 'Hit Rate' : 'Accuracy Trends'} Data ({trendsData.length} records)
          </h4>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          {trendsData.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No {data.isHitRate ? 'hit rate' : 'trends'} data available for this {type}
            </div>
          ) : (
            <div className="min-w-[1000px]">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">SKU</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[150px]">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[80px]">System</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[80px]">Physical</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">
                      {data.isHitRate ? 'Hit Rate' : 'Accuracy'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[80px]">Variance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[100px]">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">Audit Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {trendsData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono">{item.sku}</td>
                      <td className="px-4 py-3 text-sm">{item.product}</td>
                      <td className="px-4 py-3 text-sm">{item.locationName}</td>
                      <td className="px-4 py-3 text-sm">{item.systemOnHand}</td>
                      <td className="px-4 py-3 text-sm">{item.physicalCount}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.accuracy >= 95 ? 'bg-green-100 text-green-800' :
                          item.accuracy >= 80 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.accuracy?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={item.variance >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {Math.abs(item.variance)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={item.varianceValue >= 0 ? 'text-green-600' : 'text-red-600'}>
                          ${Math.abs(item.varianceValue)?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.auditDate ? new Date(item.auditDate).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedInvestigationView;
